/**
 * Upload Service (AWS)
 *
 * Notes:
 * - Originally written for Ember.
 * - Converted for VueComponents.
 *
 */

// Dependencies

import MD5 from 'crypto-js/md5';
import Base64 from 'crypto-js/enc-base64';
import S3 from 'aws-sdk/clients/s3';
import range from 'lodash.range';
import clamp from 'lodash/clamp';
import sumBy from 'lodash/sumBy';
// This is a little weird: to avoid importing the whole AWS SDK, we
// first import aws-sdk/global to get the global "AWS" object,
// then import aws-sdk/lib/credentials
// which adds Credentials to that AWS object.
import AWS from 'aws-sdk/global';
import 'aws-sdk/lib/credentials';
const { Credentials } = AWS;
import SRApi from './api';

AWS.config.httpOptions.timeout = 20 * 60 * 1000

// Class

const Uploader = {
    upload(file, intentType, cb, initCb, errorCb){

        const upload = {
            type: 'upload',
            file,
            file_name: file.name,
            bytesUploaded: 0,
            isOutstanding: true,
            onInitFinish: initCb,
            onFinish: cb,
            onError: errorCb,
        };

        // Initialze Upload
        this.initUpload(file, intentType)
        .then(({uploads: remoteUploads, awsConfig}) => {

            const { partsUploaded } = remoteUploads[0];

            upload.id = remoteUploads[0].id.toString();
            upload.s3_id = remoteUploads[0].s3_id;
            upload.s3_key = remoteUploads[0].s3_key;
            upload.part_size = remoteUploads[0].part_size;

            if (upload.onInitFinish) {
                upload.onInitFinish(upload);
            }

            if(remoteUploads[0].finished_at) {
                upload.started_at = remoteUploads[0].started_at;
                upload.finished_at = remoteUploads[0].finished_at;
                upload.bytesUploaded = upload.file.size;
            } else {

                upload.started_at = remoteUploads[0].started_at;
                // console.(partsUploaded,"__ PARTS UPLOADED __");

                upload.bytesUploaded = sumBy(partsUploaded, 'size')
                this.conductUpload(upload, partsUploaded, awsConfig);

            }

        })
            .catch((e) => {
                if (upload.onError) {
                    upload.onError(e, upload);
                }
            });

        return upload;

    },

    initUpload(file, intentType){

        return this.createFileHash(file).then(hash => {
            return SRApi.post('/api/v1/upload/init', {
                    uploads: [{
                        filename: file.name,
                        filesize: file.size,
                        part_size: this.choosePartSize(file),
                        last_modified: (file.lastModified || file.lastModifiedDate).valueOf(),
                        hash,
                        intentType,
            }]})
        });

    },

    choosePartSize(file) {
        return clamp(file.size / 4, 8 * 1024 * 1024, 64 * 1024 * 1024)
    },

    // Create a crude hash for the file by hashing slices of a few
    // different parts of the file. This doesn't produce nearly as
    // robust a result as hashing the entire contents, but that
    // operation is impractical here with large files.
    // This hash, combined with other identifying data about the
    // file, should be enough to uniquely identify it.
    createFileHash(file) {

        const slices = this.extractSlices(file);
        const promises = slices.map(slice => this.readSlice(slice));

        return Promise.all(promises).then(strings => {
            return MD5(strings.join(''))
                .toString(Base64)
                .replace('+', '-')
                .replace('/', '_')
                .replace(/=+$/, '');
        });
    },

    extractSlices(file) {

        const sliceLength = 10 * 1024;

        let slices = [];
        slices.push(file.slice(0, sliceLength));
        slices.push(file.slice(-sliceLength));
        slices.push(file.slice(Math.floor(file.size / 2), sliceLength));
        return slices;

    },

    readSlice(slice) {

        return new Promise(resolve => {
            const reader = new FileReader();

            reader.onloadend = function() {
                resolve(reader.result);
            };

            reader.readAsText(slice);
        });

    },

    conductUpload(upload, partsUploaded, awsConfig) {
        const { bucket, credentials: { accessKey, secret, sessionToken } } = awsConfig;

        const s3Client = new S3({
            params: {
                Bucket: bucket,
            },
            credentials: new Credentials(accessKey, secret, sessionToken),
        });

        this.uploadNextPart(s3Client, upload, partsUploaded);
    },

    uploadNextPart(s3Client, upload, partsUploaded) {

        // Guard: Cancelled Uploads
        if (upload.isCanceled) return;

        const totalPartCount = this.getTotalPartCount(upload);
        let partNums = partsUploaded.map(item => item.partNumber);
        const partNumber = range(1, totalPartCount + 1).find(number => !partNums.includes(number));

        if (partNumber) {
            this.uploadPart(s3Client, upload, partNumber, {
                onProgress: bytesUploaded => {
                    upload.bytesUploaded = sumBy(partsUploaded, 'size') + bytesUploaded
                }
            })
            .then(part => this.uploadNextPart(s3Client, upload, [ ...partsUploaded, part ]))
            .catch((e) => {
                console.error(e);

                if (upload.onError && !upload.isCanceled) {
                    upload.onError(e, upload);
                }
            });
        } else {
            this.finish(upload, partsUploaded);
        }
    },

    uploadPart(s3Client, upload, partNumber, { onProgress }) {

        const slice = this.getSliceByIndex(upload, partNumber - 1);

        return new Promise((resolve, reject) => {
            s3Client.uploadPart({
                Body: slice,
                Key: upload.s3_key,
                PartNumber: partNumber,
                UploadId: upload.s3_id,
            }, (error, data) => {
                if (error) {
                    reject(error);
                } else {
                    onProgress(slice.size)
                    resolve({ partNumber, eTag: data.ETag, size: slice.size });
                }
            })
            .on('httpUploadProgress', progress => onProgress(progress.loaded))
        });
    },

    getTotalPartCount(upload) {
        return Math.ceil(upload.file.size / upload.part_size);
    },

    finish(upload, partsUploaded) {
        return SRApi.post('/api/v1/upload/finish/'+upload.id, {
            partsUploaded
        }).then(() => {
            upload.finished_at = new Date();
            upload.onFinish(upload);

        }).catch((e) => {
            console.error(e);

            if (upload.onError) {
                upload.onError(e, upload);
            }
        });
    },

    getSliceByIndex(upload, index) {
        const start = index * upload.part_size;
        const end = start + upload.part_size;
        return upload.file.slice(start, end);
    },

    cancel(upload) {
        upload.isCanceled = true

        if (upload.id) {
            return SRApi.delete('/api/v1/upload/' + upload.id)
        } else {
            return Promise.resolve()
        }
    },
}

export default Uploader;

export const acceptableExtensions = [
    '3g2',
    '3gp',
    'avi',
    'flv',
    'm4v',
    'mkv',
    'mov',
    'mp4',
    'mpg',
    'mts',
    'm2ts',
    'mpeg',
    'wmv',
]

export const maximumFileSize = 1024 * 1024 * 1024 * 25